import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { groupBy } from 'es-toolkit/compat';

import { type MaloupeLocale, ReviewAnalysisSentiment, ReviewAnalysisTag } from '@malou-io/package-utils';

import type { Diagnostic } from ':shared/models';

import { PartnerThemeContext } from ':core/context/partner-theme.context';
import { TagsBarChartComponent } from ':shared/components/tags-bar-chart/tags-bar-chart.component';
import { TagsDoughnutChartComponent } from ':shared/components/tags-doughnut-chart/tags-doughnut-chart.component';
import { MINIMUM_REVIEWS_WITH_TEXT, MINIMUM_TAGS_COUNT } from ':shared/constants';
import { Icon } from ':shared/enums';
import { malouChartColorGreen, malouChartColorRed } from ':shared/helpers';
import { ImagePathResolverPipe } from ':shared/pipes';

@Component({
    selector: 'app-semantic-analysis',
    imports: [TranslateModule, TagsBarChartComponent, TagsDoughnutChartComponent, ImagePathResolverPipe],
    templateUrl: './semantic-analysis.component.html',
    styleUrl: './semantic-analysis.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SemanticAnalysisComponent {
    readonly diagnostic = input.required<Diagnostic>();
    readonly locale = input.required<MaloupeLocale>();
    readonly isMalouRestaurant = input<boolean>(false);
    readonly talkToAnExpert = output<void>();

    private readonly _partnerThemeContext = inject(PartnerThemeContext);
    readonly partnerTheme = this._partnerThemeContext.activeTheme;

    readonly Icon = Icon;

    readonly reviews = computed(() => this.diagnostic().reviews ?? []);

    readonly reviewSegments = computed(() =>
        this.reviews()
            .flatMap((review) => review.semanticAnalysisSegments)
            .filter((segment) => {
                const isNeutral = segment.sentiment === ReviewAnalysisSentiment.NEUTRAL;
                const isOverallExperience = segment.category === ReviewAnalysisTag.OVERALL_EXPERIENCE;
                return !isNeutral && !isOverallExperience;
            })
    );

    readonly hasEnoughCategories = computed(() => Object.keys(groupBy(this.reviewSegments(), 'category')).length >= MINIMUM_TAGS_COUNT);

    readonly hasEnoughReviewsWithText = computed(() => this.reviews().length >= MINIMUM_REVIEWS_WITH_TEXT);

    readonly shouldDisplayChart = computed(() => {
        if (this.hasEnoughReviewsWithText()) {
            return this.hasEnoughCategories();
        }
        return true;
    });

    readonly semanticAnalysisOverview = computed(() => this.diagnostic().semanticAnalysisOverviewByLang?.[this.locale()]);

    readonly chartPositiveColor = computed(() => this.partnerTheme()?.chartPositiveColor ?? malouChartColorGreen);
    readonly chartNegativeColor = computed(() => this.partnerTheme()?.chartNegativeColor ?? malouChartColorRed);

    onTalkToAnExpert(): void {
        this.talkToAnExpert.emit();
    }
}
